"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Target, Gauge, ShieldCheck, Droplets, Cpu, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const benefits = [{
  id: "precision",
  title: "Precisão micrométrica de pulverização",
  subtitle: "Distribuição uniforme e controlo exato de caudal",
  description: "Garantia de distribuição precisa de gotas e padrões de jato consistentes para otimizar cada aplicação industrial e eliminar o desperdício de produto.",
  metric: "±1.5%",
  metricLabel: "Tolerância de caudal em bicos calibrados",
  iconName: "Target",
  highlight: "Spray pattern consistente"
}, {
  id: "efficiency",
  title: "Eficiência energética e de recursos",
  subtitle: "Redução drástica no consumo de água e químicos",
  description: "Sistemas automáticos e bicos de elevado rendimento que diminuem os tempos de ciclo, poupam matérias-primas e reduzem custos energéticos.",
  metric: "Até 45%",
  metricLabel: "Redução no consumo de fluidos e energia",
  iconName: "Gauge",
  highlight: "Sustentabilidade operacional"
}, {
  id: "reliability",
  title: "Fiabilidade contínua do processo",
  subtitle: "Operação estável e menos paragens não planeadas",
  description: "Tecnologia industrial de alta durabilidade com materiais resistentes ao desgaste e sistemas de controlo preditivo para máxima disponibilidade da linha.",
  metric: "99.8%",
  metricLabel: "Disponibilidade operacional em regime contínuo",
  iconName: "ShieldCheck",
  highlight: "Manutenção simplificada"
}];
const technicalFeatures = [{
  title: "Série FloMax® A",
  detail: "Arrefecimento e condicionamento de gases Arrefecimento antes de baghouse, precipitador eletrostático (ESP), permutador de calor, fornos Torres de arrefecimento; Arrefecimento em conduta Controlo de NOx; Remoção de SO₂"
}, {
  title: "Série FloMax® Anti-Bearding",
  detail: "Arrefecimento e condicionamento de gases; Elevada concentração de partículas de poeira; Pulverização de suspensões (slurry); Absorvedor de secagem por pulverização (spray dry absorber)"
}, {
  title: "Série FloMax® X",
  detail: "Arrefecimento e condicionamento de gases Arrefecimento em conduta; Controlo de NOx ; Absorvedor de secagem por pulverização"
}, {
  title: "Série FloMax® S",
  detail: "Arrefecimento de gases de processo; Lavagem de gases; Humidificação; Desaquecimento (desuperheating)"
}];
export default function TechnicalBenefits() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="technicalbenefits" data-nav="light" className="bg-background py-20 lg:py-28 border-b border-border transition-colors overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-16 lg:mb-20">
          <Badge variant="outline" className="mb-4 text-xs font-semibold uppercase tracking-wider text-secondary">
            Vantagens Técnicas de Engenharia
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight mb-6">
            Engenharia de precisão para máxima rentabilidade operacional.
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">Um controlo deficiente da temperatura, do volume e/ou da humidade dos gases resulta frequentemente em níveis excessivos de emissões, tempos de paragem devido a sobrecarga de precipitadores eletrostáticos (ESP), redução da eficiência ou danos em equipamentos a jusante, humedecimento e acumulação de lamas, e aumento dos custos energéticos. Conte com a Spraying Systems Co. para a expertise de processo de que necessita para evitar estes problemas. Dispomos dos recursos necessários para simular as suas condições de operação, juntamente com uma vasta gama de produtos e um historial comprovado de sucesso.</p>
        </div>

        {/* 3 Main Benefit Cards */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {benefits.map((benefit, i) => {
          const IconComponent = benefit.iconName === "Target" ? Target : benefit.iconName === "Gauge" ? Gauge : ShieldCheck;
          return <motion.div key={benefit.id} variants={itemVariants} data-index={i} className="h-full">
                <Card className="h-full flex flex-col justify-between border-border hover:border-secondary/50 shadow-sm transition-[border-color,box-shadow] duration-300">
                  <CardHeader className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-lg bg-accent text-accent-foreground">
                        <IconComponent className="size-6" />
                      </div>
                      <Badge variant="secondary" className="text-xs font-medium">
                        {benefit.highlight}
                      </Badge>
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-foreground mb-1">
                        {benefit.title}
                      </CardTitle>
                      <CardDescription className="text-xs font-medium uppercase tracking-wide text-secondary">
                        {benefit.subtitle}
                      </CardDescription>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {benefit.description}
                    </p>
                    <div className="pt-4 border-t border-border/60">
                      <div className="text-2xl font-bold text-foreground">
                        {benefit.metric}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {benefit.metricLabel}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Technical Detail & Image Showcase Bar */}
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 lg:p-10 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Visual Engineering Side */}
            <div className="lg:col-span-5 relative">
              <div className="overflow-hidden rounded-xl bg-muted aspect-4/3 relative group">
                <img data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/technical_spraying_precision_system.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/technical_spraying_precision_system.webp-wvc-sizes" alt="Sistema de pulverização técnica e ensaio de engenharia Eurocontrol" loading="lazy" decoding="async" className="w-full h-full object-cover" src="https://eurocontrol.pt/wp-content/uploads/2026/07/Captura-de-ecra-2026-07-21-as-18.34.53.png" />
                <div className="absolute inset-0 bg-primary/20 mix-blend-multiply transition-opacity group-hover:opacity-40" />
                <div className="absolute bottom-4 left-4 right-4 p-3 rounded-lg bg-card/90 backdrop-blur-xs border border-border/50 text-xs text-foreground font-medium flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Droplets className="size-4 text-secondary shrink-0" />
                    Controlo de Gota & Caudal
                  </span>
                  <span className="text-muted-foreground font-mono text-[11px]">ISO 9001</span>
                </div>
              </div>
            </div>

            {/* Specifications & Capabilities */}
            <div className="lg:col-span-7 space-y-6">
              <div>
                <div className="flex items-center gap-2 text-secondary text-sm font-semibold mb-2">
                  <Cpu className="size-4" />
                  <span>Soluções Técnicas de Elevado Desempenho</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground tracking-tight">PARA APLICAÇÕES QUE EXIGEM PRECISÃO E EFICIÊNCIA, OS BICOS FLOMAX® SUPERAM TODOS OS OUTROS</h3>
                <p className="text-sm text-muted-foreground mt-2">Os bicos FloMax não são bicos de dois fluidos tradicionais. Utilizando processos de atomização multiestágio patenteados, os bicos FloMax produzem gotas muito pequenas com eficiência excecional. O consumo de ar comprimido e de energia é baixo. Os bicos oferecem também relações de turndown significativamente mais elevadas do que os bicos de dois fluidos standard, para máxima flexibilidade operacional.</p>
              </div>

              <Separator className="bg-border" />

              {/* Technical Features Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                {technicalFeatures.map((feat, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                    <CheckCircle2 className="size-4 text-secondary shrink-0 mt-1" />
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">{feat.title}</h4>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-normal">{feat.detail}</p>
                    </div>
                  </div>)}
              </div>

              {/* CTA row */}
              <div className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <Button asChild size="lg" className="font-semibold shadow-sm">
                  <Link to="/TechnicalSolutionsPage">
                    Explorar soluções técnicas
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to="/ContactsPage">
                    Consulte os nossos engenheiros
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>;
}